import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';
import FeatureMatrixCard from '../../pricing-plans-management/components/FeatureMatrixCard';
import PlanBuilderModal from '../../pricing-plans-management/components/PlanBuilderModal';
import PricingTable from '../../pricing-plans-management/components/PricingTable';
import { PLAN_ORDER } from '../../../config/planFeatures';

const PricingPlansPanel = () => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('plans');
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);

  const loadPlans = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await adminService.getAllPricingTiersAdmin();
      const manageablePlans = (data || []).filter((tier) => Boolean(tier?.planKey));
      const canonicalPlanCount = manageablePlans.filter((tier) => PLAN_ORDER.includes(tier.planKey)).length;
      if (canonicalPlanCount !== PLAN_ORDER.length) {
        setPlans([]);
        setError('The four canonical pricing plans are unavailable. Restore the pricing catalog migration, then try again.');
        return;
      }
      setPlans(manageablePlans);
    } catch (loadError) {
      console.error('Failed to load pricing plans:', loadError);
      setError('Unable to load pricing plans. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSavePlan = async (planData) => {
    try {
      if (editingPlan?.id) {
        await adminService.updatePricingTier(editingPlan.id, planData);
        toast.success('Pricing plan updated');
      } else {
        await adminService.createPricingTier(planData);
        toast.success('Pricing plan created');
      }

      setShowPlanBuilder(false);
      setEditingPlan(null);
      await loadPlans();
    } catch (saveError) {
      console.error('Failed to save pricing plan:', saveError);
      toast.error(saveError?.message || 'Unable to save pricing plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    const plan = plans.find((item) => item.id === planId);
    if (PLAN_ORDER.includes(plan?.planKey)) {
      toast.error('Canonical pricing plans cannot be deleted. Deactivate a plan instead.');
      return;
    }
    if (!window.confirm(`Delete "${plan?.tierName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await adminService.deletePricingTier(planId);
      toast.success('Pricing plan deleted');
      await loadPlans();
    } catch (deleteError) {
      console.error('Failed to delete pricing plan:', deleteError);
      toast.error(deleteError?.message || 'Unable to delete pricing plan');
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await adminService.updatePricingTier(plan?.id, { isActive: !plan?.isActive });
      toast.success(`Plan ${plan?.isActive ? 'deactivated' : 'activated'}`);
      await loadPlans();
    } catch (toggleError) {
      console.error('Failed to update pricing plan:', toggleError);
      toast.error(toggleError?.message || 'Unable to update pricing plan');
    }
  };

  const handleReorder = async (reorderedPlans) => {
    setPlans(reorderedPlans);

    try {
      await Promise.all(
        reorderedPlans.map((plan, index) => adminService.updatePricingTier(plan.id, { sortOrder: index }))
      );
      toast.success('Plan order updated');
      await loadPlans();
    } catch (reorderError) {
      console.error('Failed to reorder pricing plans:', reorderError);
      toast.error(reorderError?.message || 'Unable to save plan order');
      await loadPlans();
    }
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowPlanBuilder(true);
  };

  const tabs = [
    { id: 'plans', label: 'Pricing Plans', icon: 'CreditCard' },
    { id: 'features', label: 'Feature Matrix', icon: 'TableProperties' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Pricing & Plans Management</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage the four approved plans and additional custom plans, including prices, limits, Stripe IDs, and feature access.
          </p>
        </div>
      </div>

      <div className="border-b border-border">
        <nav className="flex gap-6" aria-label="Pricing settings views">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={loadPlans}>
            Try Again
          </Button>
        </div>
      ) : activeTab === 'plans' ? (
        <PricingTable
          pricingTiers={plans}
          onEdit={openEditPlan}
          onCreate={() => {
            setEditingPlan(null);
            setShowPlanBuilder(true);
          }}
          onDelete={handleDeletePlan}
          onToggleActive={handleToggleActive}
          onReorder={handleReorder}
        />
      ) : (
        <FeatureMatrixCard pricingTiers={plans} />
      )}

      {showPlanBuilder && (
        <PlanBuilderModal
          onClose={() => {
            setShowPlanBuilder(false);
            setEditingPlan(null);
          }}
          plan={editingPlan}
          onSave={handleSavePlan}
          allowCustomPlanKey
          existingPlanKeys={plans.map((plan) => plan?.planKey).filter(Boolean)}
        />
      )}
    </div>
  );
};

export default PricingPlansPanel;